import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MessageCircle, Send, X } from 'lucide-react';
import { isApiEnabled } from '../../config/env';
import {
  createChatSession,
  fetchChatMessages,
  sendChatMessage,
} from '../../services/chatService';
import { useChatSocket } from '../../hooks/useChatSocket';
import { loadChatPublicDataAction } from '../../store/actions/chatActions';
import * as S from './ChatWidget.styles';

const VISITOR_KEY = 'modamai_chat_visitor';
const SESSION_KEY = 'modamai_chat_session';
const quickReplies = ['Where is my order?', 'Return policy', 'Sizing help', 'Payment methods'];
const inquiryReasons = ['Order status', 'Returns & refunds', 'Product question', 'Sizing help', 'Payment issue'];

function mapServerMessage(m) {
  return {
    id: m.id,
    senderType: m.senderType,
    content: m.body || '',
    timestamp: m.createdAt ? new Date(m.createdAt) : new Date(),
  };
}

export default function ChatWidget() {
  const dispatch = useDispatch();
  const chatState = useSelector((s) => s.chat);
  const user = useSelector((s) => s.auth?.user);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [view, setView] = useState('home');
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [awaitingReply, setAwaitingReply] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formReason, setFormReason] = useState(inquiryReasons[0]);
  const [formError, setFormError] = useState('');
  const [sessionBusy, setSessionBusy] = useState(false);

  const [visitor, setVisitor] = useState(() => {
    try {
      const raw = localStorage.getItem(VISITOR_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const [session, setSession] = useState(() => {
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const displayPromo = useMemo(() => {
    const p = chatState?.promo;
    if (!p || !p.title) return null;
    return p;
  }, [chatState?.promo]);

  const kbArticles = useMemo(() => (
    Array.isArray(chatState?.kbArticles) ? chatState.kbArticles : []
  ), [chatState?.kbArticles]);

  useEffect(() => {
    if (!isApiEnabled()) return;
    dispatch(loadChatPublicDataAction());
  }, [dispatch]);

  const onSocketMessage = useCallback((msg) => {
    const mapped = mapServerMessage(msg);
    setMessages((prev) => (prev.some((x) => x.id === mapped.id) ? prev : [...prev, mapped]));
    if (!open && (mapped.senderType === 'agent' || mapped.senderType === 'bot')) {
      setUnreadCount((c) => c + 1);
    }
    if (mapped.senderType === 'agent' || mapped.senderType === 'bot') {
      setAwaitingReply(false);
    }
  }, [open]);

  const { sendMessage } = useChatSocket({
    token: session?.token,
    conversationId: session?.conversationId,
    enabled: !!(session?.token && session?.conversationId),
    onMessage: onSocketMessage,
  });

  useEffect(() => {
    if (open) setUnreadCount(0);
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, awaitingReply, view]);

  useEffect(() => {
    if (open && view === 'chat') inputRef.current?.focus();
  }, [open, view]);

  useEffect(() => {
    if (!open || view !== 'chat' || !session?.conversationId || !session?.token) return undefined;
    let cancelled = false;
    const run = async () => {
      try {
        const data = await fetchChatMessages(session.conversationId, session.token);
        if (!cancelled && data?.success) {
          const incoming = (data.messages || []).map(mapServerMessage);
          setMessages((prev) => {
            const byId = new Map((prev || []).map((m) => [m.id, m]));
            for (const msg of incoming) byId.set(msg.id, msg);
            return Array.from(byId.values()).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
          });
        }
      } catch {
        /* ignore */
      }
    };
    run();
    const interval = setInterval(run, 3000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [open, view, session?.conversationId, session?.token]);

  const doCreateSession = async (v) => {
    setSessionBusy(true);
    try {
      const res = await createChatSession({
        name: v.name,
        email: v.email,
        reason: v.reason,
        locale: 'en',
        entryPageUrl: typeof window !== 'undefined' ? window.location.href.slice(0, 2048) : '',
      });
      const next = { conversationId: res.conversationId, token: res.token };
      setSession(next);
      localStorage.setItem(SESSION_KEY, JSON.stringify(next));
      setMessages((res.messages || []).map(mapServerMessage));
      return true;
    } catch {
      return false;
    } finally {
      setSessionBusy(false);
    }
  };

  const sendUserText = async (textRaw) => {
    const trimmed = String(textRaw || '').trim();
    if (!trimmed || !session) return;
    setAwaitingReply(true);
    let ack = await sendMessage(trimmed);
    if (!ack?.ok) {
      try {
        const httpRes = await sendChatMessage(session.conversationId, session.token, trimmed);
        ack = { ok: true, id: httpRes?.message?.id || `http-${Date.now()}` };
      } catch {
        /* ignore */
      }
    }
    if (!ack?.ok) {
      setAwaitingReply(false);
      return;
    }

    setMessages((prev) => {
      if (!ack?.id || prev.some((x) => x.id === ack.id)) return prev;
      return [...prev, { id: ack.id, senderType: 'visitor', content: trimmed, timestamp: new Date() }];
    });
    setInput('');
    setTimeout(() => setAwaitingReply(false), 12000);
  };

  const submitPrechat = async (e) => {
    e.preventDefault();
    const name = formName.trim();
    const email = formEmail.trim();
    if (name.length < 2) return setFormError('Please enter your name.');
    if (!/^\S+@\S+\.\S+$/.test(email)) return setFormError('Please enter a valid email.');
    const v = { name, email, reason: formReason };
    setVisitor(v);
    localStorage.setItem(VISITOR_KEY, JSON.stringify(v));
    const ok = await doCreateSession(v);
    if (ok) setView('chat');
    else setFormError('Could not start chat. Please try again.');
  };

  const startChat = () => {
    if (!visitor || !session) {
      setFormName(visitor?.name || '');
      setFormEmail(visitor?.email || user?.email || '');
      setFormReason(visitor?.reason || inquiryReasons[0]);
      setFormError('');
      setView('prechat');
      return;
    }
    setView('chat');
  };

  if (!isApiEnabled()) return null;

  return (
    <>
      <S.LauncherBtn type="button" onClick={() => setOpen((o) => !o)} aria-label="Open chat">
        {open ? <X size={22} /> : <MessageCircle size={24} />}
        {!open && unreadCount > 0 ? <S.Badge>{unreadCount > 9 ? '9+' : unreadCount}</S.Badge> : null}
      </S.LauncherBtn>

      <S.Panel $open={open}>
        <S.Header>
          <strong>Live chat</strong>
          <button type="button" onClick={() => setOpen(false)} style={{ border: 'none', background: 'transparent', color: 'white', cursor: 'pointer' }}>
            <X size={16} />
          </button>
        </S.Header>

        <S.Body ref={scrollRef}>
          {view === 'home' && (
            <>
              <S.Card>
                <div style={{ fontWeight: 700 }}>{displayPromo?.title || 'Need help with your order?'}</div>
                <div style={{ fontSize: 13, marginTop: 6 }}>{displayPromo?.description || 'Our support team can assist with order status, returns, and product questions.'}</div>
                {displayPromo?.code ? (
                  <div style={{ marginTop: 8, fontSize: 12 }}><strong>Promo:</strong> {displayPromo.code}</div>
                ) : null}
              </S.Card>
              <S.Card as="button" type="button" onClick={startChat} style={{ textAlign: 'left', cursor: 'pointer' }}>
                Start a conversation
              </S.Card>
              <S.Card>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>Quick help</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {quickReplies.map((reply) => (
                    <button key={reply} type="button" onClick={() => { startChat(); setTimeout(() => sendUserText(reply), 250); }} style={{ border: '1px solid #ddd', background: '#fff', borderRadius: 999, padding: '5px 10px', cursor: 'pointer' }}>
                      {reply}
                    </button>
                  ))}
                </div>
              </S.Card>
            </>
          )}

          {view === 'help' && (
            <S.Card>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Knowledge base</div>
              {(kbArticles.length ? kbArticles : []).map((a) => (
                <div key={a.id} style={{ borderTop: '1px solid #eee', paddingTop: 8, marginTop: 8 }}>
                  <div style={{ fontWeight: 600 }}>{a.question}</div>
                  <div style={{ fontSize: 13, marginTop: 4 }}>{a.answer}</div>
                </div>
              ))}
              {!kbArticles.length ? <div style={{ fontSize: 13 }}>No knowledge base articles available yet.</div> : null}
            </S.Card>
          )}

          {view === 'prechat' && (
            <S.Card as="form" onSubmit={submitPrechat}>
              <div style={{ fontWeight: 700, marginBottom: 10 }}>Start chat</div>
              <label htmlFor="modamai-chat-name" style={{ fontSize: 12 }}>Name</label>
              <input id="modamai-chat-name" value={formName} onChange={(e) => setFormName(e.target.value)} style={{ width: '100%', marginTop: 4, marginBottom: 8, padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd' }} />
              <label htmlFor="modamai-chat-email" style={{ fontSize: 12 }}>Email</label>
              <input id="modamai-chat-email" type="email" value={formEmail} onChange={(e) => setFormEmail(e.target.value)} style={{ width: '100%', marginTop: 4, marginBottom: 8, padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd' }} />
              <label htmlFor="modamai-chat-reason" style={{ fontSize: 12 }}>Reason</label>
              <select id="modamai-chat-reason" value={formReason} onChange={(e) => setFormReason(e.target.value)} style={{ width: '100%', marginTop: 4, padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd' }}>
                {inquiryReasons.map((r) => <option key={r}>{r}</option>)}
              </select>
              {formError ? <div style={{ color: '#dc2626', fontSize: 12, marginTop: 8 }}>{formError}</div> : null}
              <button type="submit" disabled={sessionBusy} style={{ marginTop: 10, width: '100%', padding: '9px 12px', borderRadius: 8, border: 'none', background: '#111', color: '#fff', cursor: 'pointer' }}>
                {sessionBusy ? 'Starting...' : 'Start chat'}
              </button>
            </S.Card>
          )}

          {view === 'chat' && (
            <>
              {messages.map((m) => {
                const mine = m.senderType === 'visitor';
                return (
                  <S.MsgRow key={m.id} $mine={mine}>
                    <S.Bubble $mine={mine}>{m.content}</S.Bubble>
                  </S.MsgRow>
                );
              })}
              {awaitingReply ? <div style={{ fontSize: 12, color: '#666' }}>Waiting for reply...</div> : null}
            </>
          )}
        </S.Body>

        {view === 'chat' && session ? (
          <S.Footer>
            <S.Input
              ref={inputRef}
              value={input}
              placeholder="Type a message..."
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendUserText(input);
                }
              }}
            />
            <S.Send type="button" onClick={() => sendUserText(input)} aria-label="Send message">
              <Send size={16} />
            </S.Send>
          </S.Footer>
        ) : null}

        {view !== 'chat' ? (
          <S.TabRow>
            <S.TabBtn type="button" $active={view === 'home'} onClick={() => setView('home')}>Home</S.TabBtn>
            <S.TabBtn type="button" $active={view === 'prechat'} onClick={startChat}>Messages</S.TabBtn>
            <S.TabBtn type="button" $active={view === 'help'} onClick={() => setView('help')}>Help</S.TabBtn>
          </S.TabRow>
        ) : null}
      </S.Panel>
    </>
  );
}

